import {
  type CatalogLocale,
  LEGACY_BASE_LOCALE,
  normalizeCatalogLocale,
  resolveCatalogLanguageSettings,
} from '@/utils/languagePolicy'

export const PROJECT_LANGUAGE_SETTINGS_PREFERENCE_KEY =
  'project-language-settings'

const PROJECT_LANGUAGE_SETTINGS_PREFERENCE_PREFIX = `${PROJECT_LANGUAGE_SETTINGS_PREFERENCE_KEY}:`

export type ProjectLanguageSettings = {
  supportedLocales: CatalogLocale[]
  defaultLocale: CatalogLocale
}

export type ProjectLanguageSettingsProjectLike = {
  preferences?: unknown
}

const parseProjectLanguageSettingsPreference = (
  value: unknown,
): ProjectLanguageSettings | null => {
  if (typeof value !== 'string') return null
  if (!value.startsWith(PROJECT_LANGUAGE_SETTINGS_PREFERENCE_PREFIX)) {
    return null
  }

  try {
    const parsed = JSON.parse(
      value.slice(PROJECT_LANGUAGE_SETTINGS_PREFERENCE_PREFIX.length),
    ) as Partial<ProjectLanguageSettings>

    return resolveCatalogLanguageSettings({
      supportedLocales: parsed.supportedLocales,
      defaultLocale: parsed.defaultLocale,
    })
  } catch {
    return null
  }
}

export function readProjectLanguageSettingsPreference(
  preferences: unknown,
): ProjectLanguageSettings | null {
  if (!Array.isArray(preferences)) return null

  for (const preference of preferences) {
    const settings = parseProjectLanguageSettingsPreference(preference)
    if (settings) return settings
  }

  return null
}

export function hasProjectLanguageSettingsPreference(
  project?: ProjectLanguageSettingsProjectLike | null,
): boolean {
  return readProjectLanguageSettingsPreference(project?.preferences) !== null
}

export function resolveProjectLanguageSettings({
  project,
}: {
  project?: ProjectLanguageSettingsProjectLike | null
}): ProjectLanguageSettings {
  const storedSettings = readProjectLanguageSettingsPreference(
    project?.preferences,
  )
  if (storedSettings) return storedSettings

  return resolveCatalogLanguageSettings({
    supportedLocales: [LEGACY_BASE_LOCALE],
    defaultLocale: LEGACY_BASE_LOCALE,
  })
}

export function resolveEffectiveCatalogLanguageSettings({
  projectLanguageSettings,
}: {
  projectLanguageSettings: ProjectLanguageSettings
}): ProjectLanguageSettings {
  return projectLanguageSettings
}

export function resolveCanonicalCatalogLanguageState({
  languageSettings,
  contentLocale,
}: {
  languageSettings: ProjectLanguageSettings
  contentLocale?: unknown
}): ProjectLanguageSettings & { contentLocale: CatalogLocale } {
  const normalizedContentLocale = normalizeCatalogLocale(contentLocale)

  return {
    supportedLocales: languageSettings.supportedLocales,
    defaultLocale: languageSettings.defaultLocale,
    contentLocale:
      normalizedContentLocale &&
      languageSettings.supportedLocales.includes(normalizedContentLocale)
        ? normalizedContentLocale
        : languageSettings.defaultLocale,
  }
}

export function buildProjectLanguagePreferences({
  existingPreferences,
  settings,
}: {
  existingPreferences: unknown
  settings: {
    supportedLocales?: unknown
    defaultLocale?: unknown
  }
}) {
  const preferences = Array.isArray(existingPreferences)
    ? existingPreferences.filter(
        (preference): preference is string => typeof preference === 'string',
      )
    : []

  const filteredPreferences = preferences.filter(
    (preference) =>
      !preference.startsWith(PROJECT_LANGUAGE_SETTINGS_PREFERENCE_PREFIX),
  )
  const resolvedSettings = resolveCatalogLanguageSettings(settings)

  return [
    ...filteredPreferences,
    `${PROJECT_LANGUAGE_SETTINGS_PREFERENCE_PREFIX}${JSON.stringify(
      resolvedSettings,
    )}`,
  ]
}

export function buildProjectLanguageProjectUpdate({
  project,
  settings,
}: {
  project?: ProjectLanguageSettingsProjectLike | null
  settings: {
    supportedLocales?: unknown
    defaultLocale?: unknown
  }
}) {
  return {
    preferences: buildProjectLanguagePreferences({
      existingPreferences: project?.preferences,
      settings,
    }),
  }
}
